import { saveAs } from "file-saver";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";

import ArticleIcon from "@mui/icons-material/Article";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";

// import CopyFileModal from '../../modal/CopyFile'
import { SessionView } from "../../session/Session";
import {
  GraphResToTree,
  GraphResponse,
  NodeAction,
  NodeHooks,
  Tree,
} from "./NodeData";
import RenderGraph from "./Render";
import CreateModal from "../../modals/CreateComponent";

import { Box, IconButton, useTheme } from "@mui/material";
import { getHeaders } from "../../../utils/common";
import StatusBar from "../../../components/StatusBar";
import Loading from "../../../Loading";
import ErrorDisplay from "../../../components/error/ErrorDisplay";
import { DeployComponent } from "../../deploy/Deploy";

type appParams = {
  sessionID: string;
  fileName: string;
};

/**
 * Main application component for managing live data and configuration views.
 *
 * @returns {JSX.Element} The JSX element representing the main application component.
 */
export default function App() {
  const theme = useTheme();
  const navigate = useNavigate();

  // Copy file modal
  const [copyFileModalIsOpen, setCopyFileModalIsOpen] = useState(false);
  const [deploySelected, setDeploySelected] = useState<string>("");

  const { sessionID, fileName } = useParams<appParams>();
  const configURL = `/app/view/s/${sessionID}/${fileName}/config`;

  const [isLoading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<Error | null>(null);
  const [tree, setTree] = useState<Tree>({
    read_only: true,
    has_undo: false,
    has_redo: false,
  });
  const [sessView, setSessView] = useState<SessionView | null>(null);

  // Node hooks
  const [createModalIsOpen, setCreateModalIsOpen] = useState(false);
  const [nodeActionsCtx, setNodeActionsCtx] = useState<NodeAction[]>([]);

  // Set the default view to match this one.
  localStorage.setItem("defaultFileView", "graph");

  const myHeaders = getHeaders();
  myHeaders.append("Content-Type", "application/json");

  // An editing action that doesn't require a modal to display further choices.
  const headlessAction = useCallback(
    (ctx: any) => {
      fetch(`/mqttapi/v1/session/${sessionID}/file/${fileName}/tree/node`, {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify(ctx),
      })
        .then(async (response) => {
          if (response.ok) {
            return response.json();
          }
          const text = await response.text();
          throw new Error(`${response.statusText}: ${text}`);
        })
        .then((res: GraphResponse) => {
          setTree(GraphResToTree(res));
        })
        .catch((error) => setFetchError(error));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionID, fileName, setFetchError, setTree]
  );

  const closeCreateModal = () => setCreateModalIsOpen(false);
  const nodeHooks: NodeHooks = {
    openActionModal: (actions: NodeAction[]) => {
      setNodeActionsCtx(actions);
      setCreateModalIsOpen(true);
    },
    headlessAction: headlessAction,
    isReadOnly: () => {
      return tree.read_only;
    },
  };

  const fetchURL = `/mqttapi/v1/session/${sessionID}/file/${fileName}/tree`;

  useEffect(() => {
    let subscribed = true;

    fetch(fetchURL, { headers: myHeaders, method: "GET" })
      .then(async (response) => {
        if (response.ok) {
          setFetchError(null);
          return response.json();
        }
        const text = await response.text();
        throw new Error(`${response.statusText}: ${text}`);
      })
      .then((res: GraphResponse) => {
        if (!subscribed) {
          return;
        }
        setTree(GraphResToTree(res));
        setSessView(res.view || null);
      })
      .catch((error) => (subscribed ? setFetchError(error) : null))
      .finally(() => (subscribed ? setLoading(false) : null));

    return () => {
      subscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchURL, sessionID, fileName]);

  const downloadFile = () => {
    const url = `/mqttapi/v1/session/${sessionID}/download/${fileName}`;
    fetch(url, { headers: myHeaders, method: "GET" })
      .then(async (response) => {
        if (response.ok) {
          return response.json();
        }
        const text = await response.text();
        throw new Error(`${response.statusText}: ${text}`);
      })
      .then((res) => {
        const blob = new Blob([res], {
          type: "application/json;charset=utf-8",
        });
        saveAs(blob, `${fileName}`);
      })
      .catch((error) => console.log(error));
  };

  return (
    <Box sx={{ height: "88vh", width: "100%", padding: theme.spacing(2) }}>
      {/* <CopyFileModal
        sessionID={sessionID}
        target={decodeURIComponent(fileName)}
        onCopy={(sessionId: string, file_name: string) => {
          const openType = 'graph'
          navigate(`/app/view/s/${sessionId}/${encodeURIComponent(file_name)}/${openType}`)
        }}
        isOpen={copyFileModalIsOpen}
        closeModal={() => setCopyFileModalIsOpen(false)}
        organisation={
          sessView?.organisation_name
            ? {
                id: sessView.owner_id,
                name: sessView.organisation_name,
              }
            : undefined
        }
      /> */}
      <CreateModal
        sessionID={sessionID || ""}
        fileName={fileName || ""}
        setTree={setTree}
        isOpen={createModalIsOpen}
        closeModal={closeCreateModal}
        actions={nodeActionsCtx}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <IconButton
          title="switch to config view"
          onClick={() => navigate(configURL)}
        >
          <ArticleIcon />
        </IconButton>
        <IconButton
          title="copy file to..."
          onClick={() => {
            setCopyFileModalIsOpen(true);
          }}
        >
          <ContentCopyIcon />
        </IconButton>
        <IconButton title="download file" onClick={downloadFile}>
          <DownloadIcon />
        </IconButton>
        <DeployComponent
          deploySelected={deploySelected}
          setDeploySelected={setDeploySelected}
        />
      </Box>

      <Box sx={{ height: "inherit", width: "100%" }}>
        {isLoading ? (
          <Loading />
        ) : fetchError !== null ? (
          <>
            <ErrorDisplay err={fetchError} url={fetchURL}>
              {fetchError.message.includes(
                "failed to unmarshal target file"
              ) && (
                <p>
                  This looks like a YAML parse error, try switching to the{" "}
                  <RouterLink to={configURL}>config view</RouterLink> in order
                  to fix it by hand.
                </p>
              )}
            </ErrorDisplay>
          </>
        ) : (
          <RenderGraph
            tree={tree}
            setTree={setTree}
            nodeHooks={nodeHooks}
            deploySelected={deploySelected}
          />
        )}
      </Box>
    </Box>
  );
}
